import * as React from 'react'
import {ReactNode, useContext} from 'react'
import {Box, Button, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import {Nullable} from '../../../app/TypeHelpers'
import {MESSAGE_TYPE} from '../../../CodeMaster'
import {createIconWithLevel} from '../../../styledComponents/adminPage/IconCreator'
import {FullWidthLeftRowFlexContainer, LeftRowFlexContainer} from '../../../styledComponents/FlexComponents'
import styled from 'styled-components'
import {AppAdminContext} from '../../../app/AppAdminContext'
import {Colors, FontFamily} from '../../../styledComponents/Constants'
import {UnstyledLinkStyledButton} from '../../../styledComponents/Button'
import {AdminDialogBase} from './AdminDialogBase'

export interface DialogButtonParams {
    buttonLabel: string
    buttonHandler: (...params: any) => void
    isDefault: boolean
    color?: Colors
}

export interface DialogParams {
    title: string
    titleIcon?: ReactNode
    errorLevel: MESSAGE_TYPE
    message: ReactNode
    buttonProps: DialogButtonParams[]
}

interface DialogComponentProps {
    params: Nullable<DialogParams>
    open: boolean
}

export const DialogComponent: React.FC<DialogComponentProps> = props => {
    const {open, params} = props
    const {showSpModePage} = useContext(AppAdminContext)

    if (!params) {
        return null
    }

    const getTitleIcon = () => {
        return params.titleIcon ?
            params.titleIcon : createIconWithLevel(params.errorLevel)
    }

    return showSpModePage ?
        <SPDialog
            open={open}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <SPDialogTitle id="alert-dialog-title">
                {createIconWithLevel(params.errorLevel)}{params.title}
            </SPDialogTitle>
            <SPDialogContent>
                {typeof (params.message) === 'string' ?
                    <SPDialogContentText id="alert-dialog-description">
                        {params.message}
                    </SPDialogContentText>
                    :
                    <div id="alert-dialog-description">
                        {params.message}
                    </div>
                }
            </SPDialogContent>
            <SPDialogActions>
                <FullWidthLeftRowFlexContainer>
                    {
                        params.buttonProps.map((buttonProp, index) => (
                            <SPDialogActionButton
                                key={index}
                                onClick={buttonProp.buttonHandler}
                            >
                                {buttonProp.buttonLabel}
                            </SPDialogActionButton>
                        ))
                    }
                </FullWidthLeftRowFlexContainer>
            </SPDialogActions>
        </SPDialog>
        :
        <AdminDialogBase
            open={open}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                <LeftRowFlexContainer>
                    {getTitleIcon()}
                    <Box width={'5px'}/>
                    {params.title}
                </LeftRowFlexContainer>
            </DialogTitle>
            <StyledDialogContent>
                {typeof (params.message) === 'string' ?
                    <StyledDialogContentText id="alert-dialog-description">
                        {params.message}
                    </StyledDialogContentText>
                    :
                    <div id="alert-dialog-description">
                        {params.message}
                    </div>
                }
            </StyledDialogContent>
            <DialogActions>
                {
                    params.buttonProps.map((buttonProp, index) => {
                        const ButtonElement =
                            <Button
                                color={buttonProp.color ? 'inherit' : 'primary'}
                                onClick={buttonProp.buttonHandler}
                            >
                                {buttonProp.buttonLabel}
                            </Button>
                        return (
                            <React.Fragment key={index}>
                                {
                                    buttonProp.color ?
                                        <Box color={buttonProp.color}>{ButtonElement}</Box> :
                                        <div key={index}>{ButtonElement}</div>
                                }
                            </React.Fragment>
                        )})
                }
            </DialogActions>
        </AdminDialogBase>

}
DialogComponent.displayName = 'DialogComponent'

const StyledDialogContent = styled(DialogContent)`
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const SPDialog = withStyles({
    paper: {
        maxWidth: '420px',
        minWidth: '290px',
    },
})(AdminDialogBase)

const SPDialogTitle = styled(DialogTitle)`
  &.MuiDialogTitle-root.MuiTypography-h6 {
    padding: 15px 15px 0 15px;
    color: ${Colors.Body};
    font-size: 16px;
    font-family: ${FontFamily.ResidentDefault};
    display: flex;
    align-items: center;
  }

  &.MuiTypography-h6 svg {
    font-size: 20px;
    margin-right: 5px;
  }
`

const SPDialogContent = styled(StyledDialogContent)`
  &.MuiDialogContent-root {
    padding: 10px 15px 20px 15px;
  }

  & .MuiTypography-root {
    color: ${Colors.Body};
    font-size: 14px;
    font-family: ${FontFamily.ResidentDefault};
  }
`

const SPDialogContentText = styled(DialogContentText)`
  &.MuiDialogContentText-root {
    white-space: pre-line;
      color: rgba(0, 0, 0, 0.87);
  }
`

const SPDialogActions = styled(DialogActions)`
  &.MuiDialogActions-root {
    padding: 0;
  }
`

const SPDialogActionButton = styled(UnstyledLinkStyledButton)`
  margin: 0;
  padding: 10px 10px 15px;
  width: 100%;
  border-top: ${Colors.AccentLight} solid 1px;
  white-space: nowrap;
  color: ${Colors.AdminPrimary};
  font-size: 16px;

  &:not(:last-child) {
    border-right: ${Colors.AccentLight} solid 1px;
  }
`

const StyledDialogContentText = styled(DialogContentText)`
  &.MuiDialogContentText-root {
    white-space: pre-wrap;
      color: rgba(0, 0, 0, 0.87);
  }
`
